import React, { useState } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { PencilIcon } from "@heroicons/react/24/outline";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Define model to zone mapping
const modelToZoneMap = {
  Readaloud: "Speaking",
  RepeatSentence: "Speaking",
  DescribeImage: "Speaking",
  RetellLecture: "Speaking",
  AnswershortQuestion: "Speaking",
  SummarizeSpokenText: "Listening",
  WritefromDictation: "Listening",
  HighlightSummary: "Listening",
  MissingWord: "Listening",
  IncorrectWord: "Listening",
  SummarizeText: "Writing",
  WriteEssay: "Writing",
  SingleAnswer: "Reading",
  DoubleAnswer: "Reading",
  ReorderParagraph: "Reading",
  FillInTheBlank: "Reading",
};

export default function QuestionTable({
  questions,
  currentPage = 1,
  limit = 10,
  handleEditClick,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);

  const highlightText = (text, query) => {
    if (!text) return "";
    if (typeof text !== "string") return text;
    if (!query) return text;

    const parts = text.split(new RegExp(`(${query})`, "i"));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={index} className="bg-yellow-300">{part}</span>
      ) : part
    );
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredQuestions = questions.filter((question) => {
    const isTitleMatch = typeof question.q_title === 'string' &&
      question.q_title.toLowerCase().includes(searchQuery.toLowerCase());
    const isIdMatch = question._id !== null && question._id !== undefined &&
      question._id.toString().includes(searchQuery);

    return isTitleMatch || isIdMatch;
  });

  const updatedQuestions = filteredQuestions.map((question) => ({
    ...question,
    zone: modelToZoneMap[question.modelName] || "Unknown",
  }));

  // Function to handle delete action using fetch API
  const handleDeleteClick = async (id, modelName) => {
    try {
      const response = await fetch(`/api/admin/questions/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ model: modelName.toLowerCase() }), // Convert model name to lower case
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete question');
      }

      toast.success(data.message); // Notify success
      // Optionally, refresh or update the state after deletion
      // refreshQuestions();

    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete the question'); // Notify failure
    }
  };

  const handleConfirmDelete = () => {
    const questionToDelete = updatedQuestions.find(q => q._id === currentQuestionId);

    // Check if the question exists before attempting to access modelName
    if (!questionToDelete) {
      alert('Question not found. Please refresh the list and try again.');
      setIsModalOpen(false);
      return;
    }

    const modelName = questionToDelete.modelName; // Get model name
    handleDeleteClick(currentQuestionId, modelName); // Pass model name to delete function
    setIsModalOpen(false);
    setCurrentQuestionId(null);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by title or ID..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="border border-gray-300 rounded px-3 py-2 w-full"
        />
      </div>
      <table className="min-w-full">
        <thead>
          <tr className="text-left text-sm font-semibold text-gray-500">
            <th className="pb-4">S.No</th>
            <th className="pb-4">ID</th>
            <th className="pb-4">Title</th>
            <th className="pb-4">Zone</th>
            <th className="pb-4">Section</th>
            <th className="pb-4">Actions</th>
          </tr>
        </thead>
        <tbody>
          {updatedQuestions.length > 0 ? (
            updatedQuestions.map((question, index) => {
              const serialNumber = (currentPage - 1) * limit + index + 1;
              return (
                <tr key={question._id} className="text-sm text-gray-700">
                  <td className="py-2">{serialNumber}</td>
                  <td className="py-2">{question.id}</td>
                  <td className="py-2">{highlightText(question.q_title, searchQuery)}</td>
                  <td className="py-2 capitalize">{question.zone}</td>
                  <td className="py-2">{question.modelName}</td>
                  <td className="py-2 flex space-x-2">
                    <button
                      className="flex items-center bg-blue-500 text-white rounded-lg px-3 py-1 transition duration-200 hover:bg-blue-600"
                      onClick={() => handleEditClick(question._id)}
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      className="flex items-center bg-red-500 text-white rounded-lg px-3 py-1 transition duration-200 hover:bg-red-600"
                      onClick={() => {
                        setIsModalOpen(true);
                        setCurrentQuestionId(question._id);
                      }}
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="6" className="text-center py-4 text-gray-500">
                No questions available.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-lg font-semibold mb-4">Confirm Delete</h2>
            <p className="mb-4">Are you sure you want to delete this question?</p>
            <div className="flex justify-between">
              <button
                className="bg-green-500 text-white rounded-lg px-4 py-2 transition duration-200 hover:bg-green-600"
                onClick={() => handleConfirmDelete(false)} // Temporary delete
              >
                Temporary Delete
              </button>
              <button
                className="bg-red-500 text-white rounded-lg px-4 py-2 transition duration-200 hover:bg-red-600"
                onClick={() => handleConfirmDelete(true)} // Permanent delete
              >
                Permanent Delete
              </button>
            </div>
            <button
              className="mt-4 text-gray-500 hover:text-gray-700"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <ToastContainer /> {/* Add ToastContainer for notifications */}
    </div>
  );
}
